import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Chip, Stack, IconButton, Tooltip,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Swal from 'sweetalert2';
import request from '../../../services/Request';
import ModernTable, { ModernPageHeader } from '../components/ModernTable';

const BASE = '/external/admin';

export default function ApiKeys() {
  const theme = useTheme();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [generatedKey, setGeneratedKey] = useState('');
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    request({ method: 'get', url: `${BASE}/apikeys` }).then((res) => {
      if (!cancelled) { setKeys(res.data); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [refresh]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const res = await request({ method: 'post', url: `${BASE}/apikeys`, data: { name: newName } });
      setGeneratedKey(res.data.key);
      setNewName('');
      refresh ? setRefresh(false) : setRefresh(true);
    } catch {
      Swal.fire({ icon: 'error', text: 'Failed to create API key' });
    }
  };

  const handleDelete = async (id, name) => {
    const confirm = await Swal.fire({
      title: `Delete "${name}"?`,
      text: 'This key will stop working immediately.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: theme.palette.error.main,
    });
    if (!confirm.isConfirmed) return;
    await request({ method: 'delete', url: `${BASE}/apikeys/${id}` });
    Swal.fire({ icon: 'success', text: 'API key deleted', timer: 1200, showConfirmButton: false });
    refresh ? setRefresh(false) : setRefresh(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    Swal.fire({ icon: 'success', text: 'Copied to clipboard', timer: 1200, showConfirmButton: false });
  };

  const columns = [
    {
      title: 'Name', field: 'name',
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
          {row.name}
        </Typography>
      ),
    },
    {
      title: 'Key', field: 'key',
      render: (row) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
            {row.key?.slice(0, 20)}...
          </Typography>
          <Tooltip title="Copy full key">
            <IconButton size="small" onClick={() => copyToClipboard(row.key)}>
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
    {
      title: 'Created', field: 'createdAt',
      render: (row) => (
        <Typography variant="body2" color="text.secondary">
          {new Date(row.createdAt).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      title: 'Action', field: '_id',
      render: (row) => (
        <Tooltip title="Delete key">
          <IconButton size="small" onClick={() => handleDelete(row._id, row.name)}
            sx={{ color: theme.palette.error.main, '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) } }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <ModernPageHeader
        title="API Keys"
        subtitle="Keys for external systems to authenticate with the Magex API"
        icon={VpnKeyIcon}
        buttonText="Create Key"
        buttonIcon={AddIcon}
        onButtonClick={() => setCreateOpen(true)}
      />

      <ModernTable
        isLoading={loading}
        columns={columns}
        data={keys}
        options={{ actionsColumnIndex: -1, pageSize: 10, search: false }}
      />

      {/* Create Key Dialog */}
      <Dialog open={createOpen && !generatedKey} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Create API Key</DialogTitle>
        <DialogContent>
          <TextField
            label="Key Name (e.g. TCG Production)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            fullWidth autoFocus variant="outlined"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={!newName.trim()}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Show Generated Key Dialog */}
      <Dialog open={!!generatedKey} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>API Key Created</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Copy this key now — it will not be shown again.
          </Typography>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            borderRadius: 2, p: 2,
          }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1, fontSize: 13 }}>
              {generatedKey}
            </Typography>
            <Tooltip title="Copy">
              <IconButton onClick={() => copyToClipboard(generatedKey)} size="small" sx={{ color: theme.palette.primary.main, flexShrink: 0 }}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setGeneratedKey(''); setCreateOpen(false); }} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
